import { InfoIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Divider,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
  VStack
} from "@chakra-ui/react";
import { useEffect, useState } from "react";

import { useAppState } from "~/hooks/useAppState";

interface BuildInfoData {
  commit: {
    hash: string;
    hashShort: string;
    branch: string;
  };
  build: {
    timestamp: string;
    timestampLocal: string;
    unixTime: number;
  };
  environment: {
    name: "production" | "staging" | "development";
    nodeEnv: string;
    platform: "digitalocean" | "local";
  };
}

/**
 * Format time as relative (e.g., "2 hours ago")
 */
function getRelativeTime(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
  return then.toLocaleDateString();
}

/**
 * Get environment badge color
 */
function getEnvironmentColor(env: string): string {
  switch (env) {
    case "production":
      return "green";
    case "staging":
      return "yellow";
    case "development":
      return "blue";
    default:
      return "gray";
  }
}

export const BuildInfo = () => {
  const [buildInfo, setBuildInfo] = useState<BuildInfoData | null>(null);
  const [loading, setLoading] = useState(true);
  const { isOpen, onToggle } = useDisclosure();
  const { userInfo } = useAppState("userInfo");
  useEffect(() => {
    // Always show for now (can be toggled off via localStorage)
    const isDevelopment = import.meta.env.MODE === "development";
    const hideInDev = localStorage.getItem("hideBuildInfo") === "true";

    if (isDevelopment && hideInDev) {
      setLoading(false);
      return;
    }

    // Fetch build info with cache-busting
    fetch(`/build-info.json?t=${Date.now()}`, {
      cache: "no-cache",
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache"
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Build info not found");
        }
        return response.json();
      })
      .then((data) => {
        setBuildInfo(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load build info:", error);
        setLoading(false);
      });
  }, []);

  // Don't render anything if loading or no build info
  if (loading || !buildInfo) {
    return null;
  }

  const relativeTime = getRelativeTime(buildInfo.build.timestamp);
  const envColor = getEnvironmentColor(buildInfo.environment.name);

  return (
    <>
      <Box fontSize="xs" color="gray.600">
        <Flex align="center" gap={2}>
          {/* Compact view */}
          <HStack spacing={2} visibility={"hidden"}>
            <Badge colorScheme={envColor} fontSize="xs" textTransform="capitalize">
              {buildInfo.environment.name}
            </Badge>

            <Divider orientation="vertical" height="14px" />

            <Tooltip label={`Commit: ${buildInfo.commit.hash}`} hasArrow>
              <Text as="span" fontFamily="mono" fontSize="xs">
                {buildInfo.commit.hashShort}
              </Text>
            </Tooltip>

            {buildInfo.commit.branch !== "unknown" && (
              <>
                <Text as="span" color="gray.400">
                  •
                </Text>
                <Text as="span">{buildInfo.commit.branch}</Text>
              </>
            )}

            <Text as="span" color="gray.400">
              •
            </Text>
            <Text as="span">{relativeTime}</Text>
          </HStack>

          {/* Info button to open modal */}
          <IconButton
            aria-label="Show build details"
            icon={<InfoIcon />}
            size="xs"
            variant="ghost"
            onClick={onToggle}
          />
        </Flex>
      </Box>

      {/* Modal with build details */}
      <Modal isOpen={isOpen} onClose={onToggle} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Build Information</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={3}>
              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Environment:
                </Text>
                <Badge colorScheme={envColor} fontSize="sm" textTransform="capitalize">
                  {buildInfo.environment.name}
                </Badge>
              </HStack>

              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Commit:
                </Text>
                <Text fontFamily="mono" fontSize="sm">
                  {buildInfo.commit.hash}
                </Text>
              </HStack>

              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Branch:
                </Text>
                <Text>{buildInfo.commit.branch}</Text>
              </HStack>

              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Built:
                </Text>
                <Text>{new Date(buildInfo.build.timestamp).toLocaleString()}</Text>
              </HStack>

              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Platform:
                </Text>
                <Badge variant="outline" fontSize="sm">
                  {buildInfo.environment.platform === "digitalocean" ? "Digital Ocean" : "Local"}
                </Badge>
              </HStack>

              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Node Env:
                </Text>
                <Text>{buildInfo.environment.nodeEnv}</Text>
              </HStack>

              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Unix Time:
                </Text>
                <Text>{buildInfo.build.unixTime}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Exam Year:
                </Text>
                <Text>{userInfo?.examYear}</Text>
              </HStack>

              <Divider my={2} />

              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Novi Key:
                </Text>
                <Text fontFamily="mono" fontSize="sm">
                  {userInfo?.noviApiKeyPrefix || "N/A"}
                </Text>
              </HStack>

              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Lingco Key:
                </Text>
                <Text fontFamily="mono" fontSize="sm">
                  {userInfo?.lingcoApiKeyPrefix || "N/A"}
                </Text>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
